import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { Backdrop, CircularProgress } from '@mui/material';
import { AxiosError } from 'axios';
import { useQueryClient } from 'react-query';
import { MongoQuery, MongoAbility, AbilityTuple } from '@casl/ability';

import { useSiteConfig } from '@frontend/features/common/components/SiteConfigContext';
import { router } from '@frontend/router/router';

import { GetMeDTO } from '@bandido/shared/dtos';
import { UserRoles } from '@bandido/shared/types';

import { PrivateLogin } from './PrivateLogin';
import { useGetMyUserAgencies, useGetMyUserData } from '../queries';
import { QueryKeys } from '../../common/types';
import { client } from '../../common/services';
import { getUserAbilities } from '../abilities';
import { Profile } from './Profile';
import { routes } from '@frontend/router/routes';
import { useWS } from '@frontend/features/common/hooks/useWS';
import { WsEventsNotifications } from '@bandido/shared/constants';
import { CONFIG } from '@frontend/config';

interface SignInValues {
  email: string;
  password: string;
  callback?: (status?: number) => void;
}

export interface ChangePasswordValues {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
  callback: (status: 'success' | 'error', statusCode: number) => void;
}

export type AuthContextState = {
  isAuthenticated: boolean;
  showLoginDrawer: boolean;

  myProfile: GetMeDTO | null;
  ability: MongoAbility<AbilityTuple, MongoQuery>;
  toggleLoginDrawer: () => void;
  toggleProfileEditor: () => void;
  checkIsLogged: () => void;
  doSignOut: () => void;
  doSignIn: (values: SignInValues) => void;
  changePassword: (values: ChangePasswordValues) => void;
  userAgencies?: number[];
};

export const AuthContext = createContext<AuthContextState>({} as AuthContextState);

interface Props {
  children: JSX.Element;
}

export const AuthContextProvider = ({ children }: Props) => {
  const queryClient = useQueryClient();
  const [showLoginDrawer, setShowLoginDrawer] = useState(false);
  const [myProfile, setMyProfile] = useState<GetMeDTO | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [showProfileEditor, setShowProfileEditor] = useState<boolean>(false);

  const {
    isConnected: notificationSocketIsConnected,
    socketRef: notificationSocketRef,
    sendMessage: notificationSendMessage,
  } = useWS(`${CONFIG.WS_URL}/notifications`);

  const ability = useMemo(() => {
    if (myProfile?.roles?.length) {
      const roles = myProfile?.roles.trim().split(',');
      return getUserAbilities(roles as UserRoles[]);
    }
    return getUserAbilities([UserRoles.REGISTERED]);
  }, [myProfile]);

  const checkUserIsCreated = useCallback(
    (error: AxiosError) => {
      if (error.response?.status === 404) {
        // no user in our db show complete user creation drawer
        setShowProfileEditor(true);
      }
    },
    [setShowProfileEditor],
  );

  const { data: userData, isLoading } = useGetMyUserData({
    onErrorCallback: checkUserIsCreated,
  });

  const { data: userAgencies } = useGetMyUserAgencies({});

  const { settings } = useSiteConfig();

  const doSignOut = useCallback(async () => {
    try {
      await client.post('auth/logout');
      setMyProfile(null);
      router.navigate('/');
      checkIsLogged();
    } catch (error) {
      console.error(error);
    }
  }, []);

  const doSignIn = useCallback(({ email, password, callback }: SignInValues) => {
    const onSubmit = async () => {
      try {
        const res = await fetch(`${CONFIG.API_URL}auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'agency-id': CONFIG.AGENCY_ID,
          },
          credentials: 'include',
          body: JSON.stringify({
            email,
            password,
          }),
        });

        if (res.ok) {
          checkIsLogged();
        } else {
          callback && callback(res.status);
        }
      } catch (e: any) {
        console.log('e', e);
      }
    };
    onSubmit();
  }, []);

  const changePassword = useCallback(
    async ({ currentPassword, newPassword, confirmNewPassword, callback }: ChangePasswordValues) => {
      try {
        await client.post('auth/change-password', {
          currentPassword,
          newPassword,
          confirmNewPassword,
        });
        callback('success', 200);
      } catch (error: any) {
        if (error.isAxiosError) {
          callback('error', error.response?.status || 0);
        } else {
          callback('error', 0);
        }
      }
    },
    [],
  );

  const checkIsLogged = useCallback(async () => {
    queryClient.invalidateQueries({ queryKey: QueryKeys.getMyUserData });
  }, [queryClient]);

  const toggleLoginDrawer = useCallback(() => setShowLoginDrawer((prevState) => !prevState), []);

  const toggleProfileEditor = useCallback(() => setShowProfileEditor((prevState) => !prevState), []);

  const doRender = useCallback(() => {
    const externalRoute = routes.external.caseSupportForms.replace(':id', '');

    if (settings?.isPrivate && isLoading && !userData) {
      return null;
    }

    if (settings?.isPrivate && !isAuthenticated && !isLoading && !userData && !location.href.includes(externalRoute)) {
      return <PrivateLogin />;
    }

    return children;
  }, [settings?.isPrivate, isAuthenticated, children, isLoading, userData]);

  useEffect(() => {
    if (userData) {
      setMyProfile(userData.data as GetMeDTO);
      setIsAuthenticated(true);
    } else {
      setMyProfile(null);
      setIsAuthenticated(false);
    }
  }, [userData]);

  useEffect(() => {
    if (notificationSocketIsConnected && myProfile?.id) {
      notificationSendMessage('joinRoom', myProfile?.id);
    }

    notificationSocketRef.current?.on(WsEventsNotifications.UPDATE, () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getNotifications] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getMyUserData] });
    });

    return () => {
      notificationSocketRef.current?.off(WsEventsNotifications.UPDATE);
    };
  }, [notificationSendMessage, notificationSocketIsConnected, myProfile, notificationSocketRef]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        showLoginDrawer,
        myProfile,
        ability,
        checkIsLogged,
        toggleProfileEditor,
        toggleLoginDrawer,
        doSignIn,
        doSignOut,
        changePassword,
        userAgencies,
      }}
    >
      {doRender()}
      <Profile isOpen={showProfileEditor} onClose={toggleProfileEditor} initialData={myProfile} />
      <Backdrop
        sx={{ color: (theme) => theme.palette.common.white, zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </AuthContext.Provider>
  );
};
